import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import {TablePerformanceSummary} from 'notebook/performance/tableLimitUtils';
import {GRAY_LIGHTER} from 'ui-library/Colors';
import {HoverStateProps} from 'ui-library/hooks/useHoverState';
import {Cell, Column, Table} from 'ui-library/simple-table/SimpleTable';
import SortableHeaderCell from 'ui-library/simple-table/SortableHeaderCell';
import useSimpleTableSortState from 'ui-library/simple-table/useSimpleTableSortState';
import sortedBy from 'util/sortedBy';

interface TablePerformanceSummaryTableProps {
  tableSummaries: Array<TablePerformanceSummary>;
  focusedTableId: string | null;
  showStepNameColumn: boolean;
  onHover: (tableId: string) => void;
  onLeave: () => void;
}

export function TablePerformanceSummaryTable({
  tableSummaries,
  focusedTableId,
  showStepNameColumn = false,
  onHover,
  onLeave,
}: TablePerformanceSummaryTableProps): JSX.Element {
  const {sortState, getHeaderCellProps} = useSimpleTableSortState('notebook-performance-table');

  const sortedRows = sortState
    ? sortedBy(tableSummaries, [
        {
          accessor: (summary: TablePerformanceSummary) => {
            if (sortState.columnKey === 'name') {
              return summary.name;
            }
            return summary[sortState.columnKey as keyof TablePerformanceSummary] ?? '';
          },
          reversed: sortState.sortDirection === 'arrow-down',
        },
      ])
    : tableSummaries;

  const getHoverProps = (rowIndex: number): HoverStateProps => {
    const {tableId} = sortedRows[rowIndex];
    return {
      onMouseEnter: () => {
        onHover(tableId);
      },
      onMouseMove: () => {
        onHover(tableId);
      },
      onMouseLeave: onLeave,
    };
  };

  /**
   * When a section of the bar in the SizeBreakdown.tsx is hovered, manually set the hovered background color in the table
   */
  const isRowHovered = (rowIndex: number): boolean => focusedTableId === sortedRows[rowIndex].tableId;

  return (
    <Table
      className={css(styles.summaryTable)}
      numRows={sortedRows.length}
      frozenHeader={true}
      overflowVisible={true}
    >
      {showStepNameColumn && (
        <Column
          header={
            <SortableHeaderCell
              {...getHeaderCellProps('documentName')}
              className={css(styles.documentNameColumn)}
            >
              Step Name
            </SortableHeaderCell>
          }
          renderCell={(rowIndex) => {
            const {documentName} = sortedRows[rowIndex];
            return (
              <Cell
                hoverProps={getHoverProps(rowIndex)}
                className={css(styles.documentNameColumn, isRowHovered(rowIndex) && styles.hoveredRow)}
              >
                <div className={css(styles.nameText)}>{documentName}</div>
              </Cell>
            );
          }}
        />
      )}
      <Column
        header={
          <SortableHeaderCell {...getHeaderCellProps('name')} className={css(styles.nameColumn)}>
            Table Name
          </SortableHeaderCell>
        }
        renderCell={(rowIndex) => {
          const {colorHex, name} = sortedRows[rowIndex];
          return (
            <Cell
              hoverProps={getHoverProps(rowIndex)}
              className={css(styles.nameColumn, isRowHovered(rowIndex) && styles.hoveredRow)}
            >
              <div className={css(styles.nameWithMarker)}>
                <div style={{backgroundColor: colorHex}} className={css(styles.coloredMarker)} />
                <div className={css(styles.nameText)}>{name}</div>
              </div>
            </Cell>
          );
        }}
      />
      <Column
        header={
          <SortableHeaderCell {...getHeaderCellProps('numRows')} className={css(styles.dataColumn)}>
            Rows
          </SortableHeaderCell>
        }
        renderCell={(rowIndex) => (
          <Cell
            hoverProps={getHoverProps(rowIndex)}
            className={css(
              styles.rightAlignedCell,
              styles.dataColumn,
              isRowHovered(rowIndex) && styles.hoveredRow
            )}
          >
            {sortedRows[rowIndex].numRows}
          </Cell>
        )}
      />
      <Column
        header={
          <SortableHeaderCell {...getHeaderCellProps('numColumns')} className={css(styles.dataColumn)}>
            Columns
          </SortableHeaderCell>
        }
        renderCell={(rowIndex) => (
          <Cell
            hoverProps={getHoverProps(rowIndex)}
            className={css(
              styles.rightAlignedCell,
              styles.dataColumn,
              isRowHovered(rowIndex) && styles.hoveredRow
            )}
          >
            {sortedRows[rowIndex].numColumns}
          </Cell>
        )}
      />
    </Table>
  );
}

const styles = StyleSheet.create({
  hoveredRow: {
    backgroundColor: GRAY_LIGHTER,
  },
  nameWithMarker: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  nameText: {
    wordBreak: 'break-word',
    overflowWrap: 'break-word',
    whiteSpace: 'pre-wrap',
  },
  summaryTable: {
    maxHeight: '425px',
    overflowY: 'auto',
  },
  coloredMarker: {
    width: '12px',
    height: '12px',
    borderRadius: '50%',
    flexShrink: 0,
  },
  rightAlignedCell: {
    textAlign: 'right',
  },
  documentNameColumn: {
    width: '100px',
  },
  nameColumn: {
    width: '345px',
  },
  dataColumn: {
    width: '100px',
  },
});
